import type { ApiRegisterUserJourneyEventParams } from '~/handlers/ports/api-register-user-journey-event.port';
import {
  registerUserJourneyEventHandler,
  UserJourneyEvent,
} from '~/handlers/register-user-journey-event';
import { useMarketingTrackingStore } from '~/stores/apostatudo/marketing-tracking';
import { useDeviceStore } from '~/stores/apostatudo/device';
import { useUserJourneyStore } from '~/stores/apostatudo/user-journey';
import { cleanObject } from '~/utils/apostatudo/clean-object';
import isDeepEqual from '~/utils/apostatudo/is-deep-equal';

type UserJourneyPayload = Omit<ApiRegisterUserJourneyEventParams, 'event'>;

export const useUserJourney = () => {
  const journeyStore = useUserJourneyStore();
  const { fingerprint } = storeToRefs(useDeviceStore());
  const marketingStore = useMarketingTrackingStore();
  const {
    utm: marketingUtm,
    google: marketingGoogle,
    facebook: marketingFacebook,
    tiktok: marketingTiktok,
    apt: marketingApt,
  } = storeToRefs(marketingStore);

  const { userId } = useUserData();
  // Detecta bots para evitar delay desnecessário que afeta LCP
  const { isBot } = usePerformanceBotBypass();

  const buildPayload = (raw: UserJourneyPayload): UserJourneyPayload => {
    const payload: UserJourneyPayload = {};

    if (raw.user?.id) payload.user = { id: raw.user.id };
    if (raw.device?.fingerprint)
      payload.device = { fingerprint: raw.device.fingerprint };
    if (typeof raw.deposit_id === 'number') payload.deposit_id = raw.deposit_id;

    const utm = cleanObject(raw.utm);

    if (utm) payload.utm = utm;

    const google = cleanObject(raw.google);

    if (google) payload.google = google;

    const facebook = cleanObject(raw.facebook);

    if (facebook) payload.facebook = facebook;

    const tiktok = cleanObject(raw.tiktok);

    if (tiktok) payload.tiktok = tiktok;

    const apt = cleanObject(raw.apt);

    if (apt) payload.apt = apt;

    const appsflyer = cleanObject(raw.appsflyer);

    if (appsflyer) payload.appsflyer = appsflyer;

    return payload;
  };

  const buildEnterSitePayload = (): UserJourneyPayload =>
    buildPayload({
      user: userId.value ? { id: String(userId.value) } : undefined,
      device: fingerprint.value
        ? { fingerprint: fingerprint.value }
        : undefined,
      utm: marketingUtm.value,
      google: marketingGoogle.value,
      facebook: marketingFacebook.value,
      tiktok: marketingTiktok.value,
      apt: marketingApt.value,
      appsflyer: journeyStore.appsflyer,
    });

  const getStoredPayload = (): UserJourneyPayload =>
    buildPayload({
      user: journeyStore.user.id ? { id: journeyStore.user.id } : undefined,
      device: journeyStore.device.fingerprint
        ? { fingerprint: journeyStore.device.fingerprint }
        : undefined,
      utm: journeyStore.utm,
      google: journeyStore.google,
      facebook: journeyStore.facebook,
      tiktok: journeyStore.tiktok,
      apt: journeyStore.apt,
      appsflyer: journeyStore.appsflyer,
    });

  const persistPayload = (payload: UserJourneyPayload) => {
    journeyStore.setUser(payload.user ?? {});
    journeyStore.setDevice(payload.device ?? {});
    journeyStore.setUtm(payload.utm ?? {});
    journeyStore.setGoogle(payload.google ?? {});
    journeyStore.setFacebook(payload.facebook ?? {});
    journeyStore.setTiktok(payload.tiktok ?? {});
    journeyStore.setApt(payload.apt ?? {});
    journeyStore.setAppsflyer(payload.appsflyer ?? {});
    journeyStore.setAcquiredAt(marketingStore.acquiredAt);
  };

  const sendEvent = async (
    event: UserJourneyEvent,
    payload: UserJourneyPayload,
  ): Promise<boolean> => {
    // Bots não enviam user journey (melhora performance em testes)
    if (isBot.value) return false;

    try {
      await registerUserJourneyEventHandler({ event, ...payload });

      return true;
    } catch (error) {
      console.error('[user-journey] failed to send event', event, error);

      return false;
    }
  };

  const enterSiteEvent = async () => {
    const payload = buildEnterSitePayload();

    if (isDeepEqual(payload, getStoredPayload())) return;

    const sent = await sendEvent(UserJourneyEvent.ENTER_SITE, payload);

    if (sent) persistPayload(payload);
  };

  const registerUserEvent = () => {
    queueMicrotask(() =>
      sendEvent(UserJourneyEvent.REGISTER_USER, getStoredPayload()),
    );
  };

  const blogPostClickEvent = (slug: string) => {
    if (!slug) return;

    const { user, device } = getStoredPayload();

    queueMicrotask(() =>
      sendEvent(UserJourneyEvent.BLOG_POST_CLICK, {
        user,
        device,
        slug,
      }),
    );
  };

  const homeMenuItemClickEvent = (
    itemId: string,
    renderedPosition?: number,
  ) => {
    if (!itemId) return;

    const { user, device } = getStoredPayload();

    queueMicrotask(() =>
      sendEvent(UserJourneyEvent.HOME_MENU_ITEM_CLICK, {
        user,
        device,
        home_menu_item_id: itemId,
        ...(typeof renderedPosition === 'number' && renderedPosition >= 1
          ? { home_menu_item_rendered_position: renderedPosition }
          : {}),
      }),
    );
  };

  const makeDepositEvent = (transactionId: string) => {
    const depositId = Number(transactionId?.match(/\d+/)?.[0]);

    if (Number.isNaN(depositId)) return;

    queueMicrotask(() =>
      sendEvent(UserJourneyEvent.MAKE_DEPOSIT, {
        ...getStoredPayload(),
        deposit_id: depositId,
      }),
    );
  };

  return {
    enterSiteEvent,
    registerUserEvent,
    makeDepositEvent,
    blogPostClickEvent,
    homeMenuItemClickEvent,
  };
};
